import React, { useEffect, useMemo } from 'react';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { useExtendUsersnap } from 'src/hooks/useExtendUsersnap';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import useRole from 'src/hooks/useRole';
import { useUserPermissionArray, UserPermissions, useUserPermissions } from 'src/hooks/useUserPermissions';
import { useValuationSettings } from 'src/hooks/useValuationSettings';
import { ValuationCountryCode } from 'src/modules/generated/api';
import UserRole from 'src/types/UserRoles';

import { columns, relevantTaxes } from './columns';
import { bidColumns } from './columns/bid-columns';
import { useBidsFilter } from './filter/useBidsFilter';
import {
  bidsOrderedTablePageIndexState,
  bidsOrderedTablePaginationState,
  bidsPastTablePageIndexState,
  bidsPastTablePaginationState,
  bidsPendingTablePageIndexState,
  bidsPendingTablePaginationState,
  bidPendingSortingState,
  bidPastSortingState,
  bidOrderedSortingState,
  carsTablePageIndexState,
  carsTablePaginationState,
  sortingState,
} from './state';
import { TableNext } from './TableNext';
import { BidTypes, BidsTableNextItem, CarsTableNextItem, StrictColumnDef, TableType } from './types';
import { useTableData } from './useTableData';

type BidsTableNextProps = {
  bidType: BidTypes;
};

export const BidsTableNext = React.memo(({ bidType }: BidsTableNextProps): JSX.Element => {
  const { hasRole } = useRole();
  const { valuationCountry, valuationType } = useValuationSettings();
  const packageViewFlagEnabled = useFeatureFlags('packageView');
  const hasPackageViewPermission = useUserPermissions(UserPermissions.PACKAGE_VIEW);
  const showPackageView = packageViewFlagEnabled && hasPackageViewPermission;
  const availableColumns: StrictColumnDef<CarsTableNextItem>[] = useMemo(
    () =>
      columns(false)
        .filter(
          (column: StrictColumnDef<CarsTableNextItem>) =>
            column.meta?.roles === undefined ||
            hasRole(UserRole.ADMIN) ||
            column.meta.roles.every(
              (role) => hasRole(role as UserRole) || relevantTaxes[valuationCountry!]?.includes(role),
            ),
        )
        .filter((column) => column.meta?.onlyForTable === undefined || column.meta.onlyForTable === bidType),
    [bidType, hasRole, valuationCountry],
  );
  // if the users valuationCountry is De then dont display the columns "marketPriceDE" and "referenceVehiclesDE"
  // CZ columns (marketPriceCZ, referenceVehiclesCZ, potential.CZ.*) are only shown to SK users
  // SK columns (marketPriceSK, referenceVehiclesSK, potential.SK.*) are only shown to CZ users
  const filteredColumns = useMemo(
    () =>
      availableColumns.filter((column) => {
        if (
          valuationCountry === ValuationCountryCode.De &&
          (column.id === 'marketPriceDE' || column.id === 'referenceVehiclesDE')
        )
          return false;
        if (
          column.id === 'marketPriceCZ' ||
          column.id === 'referenceVehiclesCZ' ||
          column.id?.startsWith('potential.CZ.')
        )
          return valuationCountry === ValuationCountryCode.Sk;
        if (
          column.id === 'marketPriceSK' ||
          column.id === 'referenceVehiclesSK' ||
          column.id?.startsWith('potential.SK.')
        )
          return valuationCountry === ValuationCountryCode.Cz;
        return true;
      }),
    [availableColumns, valuationCountry],
  );
  const permissions = useUserPermissionArray();
  const showDealer = hasRole(UserRole.ADMIN) || hasRole(UserRole.COUNTRYMANAGER);

  const availableBidColumns: StrictColumnDef<BidsTableNextItem>[] = useMemo(
    () =>
      bidColumns(bidType, showDealer)
        .filter(
          (column: StrictColumnDef<BidsTableNextItem>) =>
            column.meta?.roles === undefined ||
            hasRole(UserRole.ADMIN) ||
            column.meta.roles.every(
              (role) => hasRole(role as UserRole) || relevantTaxes[valuationCountry!]?.includes(role),
            ),
        )
        .filter((column) => column.meta?.onlyForTable === undefined || column.meta.onlyForTable === bidType)
        .filter(
          (column) =>
            column.meta?.permissions === undefined ||
            column.meta.permissions.every((permission) => permissions.includes(permission)),
        ),
    [bidType, hasRole, permissions, showDealer, valuationCountry],
  );

  const filteredColumnsWithBids = [...filteredColumns.slice(0, 4), ...availableBidColumns, ...filteredColumns.slice(4)];

  function getPaginationState() {
    switch (bidType) {
      case BidTypes.Pending:
        return bidsPendingTablePaginationState;
      case BidTypes.Past:
        return bidsPastTablePaginationState;
      case BidTypes.Orders:
        return bidsOrderedTablePaginationState;
      default:
        return carsTablePaginationState;
    }
  }

  function getIndex() {
    switch (bidType) {
      case BidTypes.Pending:
        return bidsPendingTablePageIndexState;
      case BidTypes.Past:
        return bidsPastTablePageIndexState;
      case BidTypes.Orders:
        return bidsOrderedTablePageIndexState;
      default:
        return carsTablePageIndexState;
    }
  }

  function getSortingState() {
    switch (bidType) {
      case BidTypes.Pending:
        return bidPendingSortingState;
      case BidTypes.Past:
        return bidPastSortingState;
      case BidTypes.Orders:
        return bidOrderedSortingState;
      default:
        return sortingState;
    }
  }

  const { reset: resetFilter, watch } = useBidsFilter();
  const filterState = watch();
  const [pagination, setPagination] = useRecoilState(getPaginationState());
  const [sorting] = useRecoilState(getSortingState());
  const resetPageIndexSate = useResetRecoilState(getIndex());

  useEffect(() => {
    const subscription = watch(() => {
      resetPageIndexSate();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [watch, resetPageIndexSate]);

  const {
    data: carsList,
    isLoading,
    refetch,
    isPreviousData,
  } = useTableData({
    filter: filterState,
    pagination,
    sorting,
    bidType,
  });

  useExtendUsersnap(
    {
      transformedFilter: filterState,
      pagination,
      sorting,
      valuationCountry,
      valuationType,
    },
    [filterState, pagination, sorting, valuationCountry, valuationType],
  );

  // transform the data from the api into cars to display in the table & add unique rowId for visualization
  const carsListData = carsList?.data?.reduce((acc: BidsTableNextItem[], curr) => {
    // Package bid: use single `car` field, one row per bid (only if feature enabled for user)
    if (showPackageView && curr.packageId && curr.car) {
      return [
        ...acc,
        {
          ...curr.car,
          ...curr,
          rowId: `package-${curr.id}`,
          isPackageBid: true,
        } as BidsTableNextItem,
      ];
    }

    // Non-package bid (or fallback for gated users): flatten cars array, one row per car
    const cars = (curr.cars ?? []).map((car) => ({
      ...car,
      ...curr,
      rowId: `${curr.id}-${car.id}`,
      isPackageBid: false,
    }));
    return [...acc, ...cars];
  }, [] as BidsTableNextItem[]);

  return (
    <TableNext
      tableColumns={filteredColumnsWithBids}
      totalRecords={carsList?.recordsTotal}
      data={carsListData}
      isLoading={isLoading}
      refetch={refetch}
      isPreviousData={isPreviousData}
      resetFilter={resetFilter}
      tableType={TableType.Bids}
      bidType={bidType}
      pagination={pagination}
      setPagination={setPagination}
      numbBids={carsList?.recordsTotal}
      numbCars={carsList?.carsRecordsTotal}
    />
  );
});
